import type { AutomobileCatalog } from '../types/automobile';
import { fetchAutomobileSupabaseCatalog } from './fetchAutomobileSupabaseCatalog';
import { getAutomobileModelDisplayName } from './automobileModelDisplay';
import { loadAutomobileLocalOverrides } from './automobileLocalOverrides';

const EMPTY_CATALOG: AutomobileCatalog = {
  generatedAt: '',
  brandCount: 0,
  brandsWithModels: 0,
  modelCount: 0,
  brands: [],
};

function applyLocalOverrides(catalog: AutomobileCatalog): AutomobileCatalog {
  const overrides = loadAutomobileLocalOverrides();
  const hasModel = Object.keys(overrides.modelImages).length > 0;
  const hasBrand = Object.keys(overrides.brandLogos).length > 0;
  const hasYears = Object.keys(overrides.modelYears || {}).length > 0;
  if (!hasModel && !hasBrand && !hasYears) return catalog;

  return {
    ...catalog,
    brands: catalog.brands.map((b) => {
      const logoOverride = overrides.brandLogos[b.slug];
      const models = b.models.map((m) => {
        const imgKey = `${b.slug}|${m.name}`;
        const imgOverride = overrides.modelImages[imgKey];
        const labelKey = `${b.slug}|${getAutomobileModelDisplayName(m)}`;
        const yearsOverride = overrides.modelYears?.[labelKey];

        if (!imgOverride && !yearsOverride) return m;
        return {
          ...m,
          imageUrl: imgOverride || m.imageUrl,
          imageGeneric: imgOverride ? false : m.imageGeneric,
          productionYears: yearsOverride || m.productionYears,
        };
      });
      return logoOverride ? { ...b, logoUrl: logoOverride, models } : { ...b, models };
    }),
  };
}

type Listener = () => void;

type StoreState = {
  catalog: AutomobileCatalog;
  loading: boolean;
  error: string | null;
};

const listeners = new Set<Listener>();
let loadPromise: Promise<void> | null = null;
let loadedOnce = false;
let backgroundRefreshStarted = false;

const CACHE_KEY = 'automobile-catalog-v1';
const CACHE_BUMP_KEY = 'automobile-catalog-bump';
const CACHE_TTL_MS = 15 * 60 * 1000;

type CachePayload = {
  at: number;
  catalog: AutomobileCatalog;
};

function readCachedCatalog(): AutomobileCatalog | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachePayload;
    if (Date.now() - parsed.at > CACHE_TTL_MS) return null;
    if (!parsed.catalog?.modelCount) return null;
    return parsed.catalog;
  } catch {
    return null;
  }
}

function buildInitialState(): StoreState {
  if (typeof window === 'undefined') {
    return { catalog: EMPTY_CATALOG, loading: false, error: null };
  }
  const cached = readCachedCatalog();
  if (cached) {
    loadedOnce = true;
    return {
      catalog: applyLocalOverrides(cached),
      loading: false,
      error: null,
    };
  }
  return { catalog: EMPTY_CATALOG, loading: true, error: null };
}

let state: StoreState = buildInitialState();

function writeCachedCatalog(catalog: AutomobileCatalog): void {
  try {
    const payload: CachePayload = { at: Date.now(), catalog };
    localStorage.setItem(CACHE_KEY, JSON.stringify(payload));
  } catch {
    /* depolama dolu — yoksay */
  }
}

function notify() {
  for (const listener of listeners) {
    listener();
  }
}

function setState(patch: Partial<StoreState>) {
  state = { ...state, ...patch };
  notify();
}

async function fetchWithRetry(attempts = 3): Promise<AutomobileCatalog> {
  let lastErr: unknown;
  for (let i = 0; i < attempts; i += 1) {
    try {
      return await fetchAutomobileSupabaseCatalog();
    } catch (err) {
      lastErr = err;
      if (i < attempts - 1) {
        await new Promise((r) => window.setTimeout(r, 400 * (i + 1)));
      }
    }
  }
  throw lastErr;
}

export async function loadAutomobileCatalog(opts?: { silent?: boolean }): Promise<void> {
  if (loadPromise) return loadPromise;

  loadPromise = (async () => {
    if (!opts?.silent) {
      setState({ loading: true, error: null });
    }
    try {
      const next = applyLocalOverrides(await fetchWithRetry());
      if (next.modelCount <= 0 && next.brandCount <= 0) {
        throw new Error('Supabase katalog boş döndü');
      }
      loadedOnce = true;
      setState({ catalog: next, loading: false, error: null });
      writeCachedCatalog(next);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Bilinmeyen hata';
      console.error('[otomobil] Katalog yüklenemedi:', err);
      if (!loadedOnce) {
        loadedOnce = true;
        setState({
          catalog: applyLocalOverrides(EMPTY_CATALOG),
          loading: false,
          error: message,
        });
      } else {
        setState({ loading: false, error: message });
      }
    }
  })().finally(() => {
    loadPromise = null;
  });

  return loadPromise;
}

export function subscribeAutomobileCatalog(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getAutomobileCatalogState(): StoreState {
  return state;
}

/** İlk Supabase/önbellek yüklemesi tamamlandı mı (yeni sekme marka sayfası için) */
export function isAutomobileCatalogReady(): boolean {
  return loadedOnce && !state.loading;
}

export function ensureAutomobileCatalogLoaded(): void {
  if (loadPromise) return;

  if (!loadedOnce) {
    const cached = readCachedCatalog();
    if (cached) {
      loadedOnce = true;
      setState({
        catalog: applyLocalOverrides(cached),
        loading: false,
        error: null,
      });
      void loadAutomobileCatalog({ silent: true });
      return;
    }
    setState({ loading: true, error: null });
    void loadAutomobileCatalog();
    return;
  }

  // Modül başlangıcında önbellekten hydrate edildiyse arka planda bir kez tazele
  if (!backgroundRefreshStarted && state.catalog.brandCount > 0) {
    backgroundRefreshStarted = true;
    void loadAutomobileCatalog({ silent: true });
  }
}

function bumpCatalogCacheForOtherTabs(): void {
  try {
    localStorage.removeItem(CACHE_KEY);
    localStorage.setItem(CACHE_BUMP_KEY, String(Date.now()));
  } catch {
    /* yoksay */
  }
}

/** Admin düzenlemesi sonrası site kataloğunu Supabase'ten yeniden çeker */
export async function refreshAutomobileCatalogFromSupabase(): Promise<void> {
  bumpCatalogCacheForOtherTabs();
  loadPromise = null;
  await loadAutomobileCatalog({ silent: false });
}

if (typeof window !== 'undefined' && !loadedOnce) {
  queueMicrotask(() => ensureAutomobileCatalogLoaded());
}

if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key !== CACHE_BUMP_KEY) return;
    loadPromise = null;
    void loadAutomobileCatalog({ silent: true });
  });
}
